import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { modelRouterModalityLabel, type ModelRouterModelInfo } from '@/lib/modelrouter-models';
import { cn } from '@/lib/utils';

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  models: ModelRouterModelInfo[];
  loading?: boolean;
  onRefresh?: () => void;
  allowAuto?: boolean;
  placeholder?: string;
  hint?: string;
  className?: string;
};

export function ModelRouterModelSelect({
  label,
  value,
  onChange,
  models,
  loading,
  onRefresh,
  allowAuto = true,
  placeholder = 'auto',
  hint,
  className,
}: Props) {
  const selectValue = models.some((m) => m.id === value) || (allowAuto && value === 'auto') ? value : '__custom__';

  return (
    <div className={cn('grid gap-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        {onRefresh && (
          <Button type="button" variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs" disabled={loading} onClick={onRefresh}>
            {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            Modelle laden
          </Button>
        )}
      </div>
      {models.length > 0 ? (
        <Select
          value={selectValue}
          onValueChange={(v) => {
            if (v === '__custom__') return;
            onChange(v);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Modell wählen …" />
          </SelectTrigger>
          <SelectContent>
            {allowAuto && <SelectItem value="auto">auto (Router wählt Modell)</SelectItem>}
            {models.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.id} — {modelRouterModalityLabel(m)}
              </SelectItem>
            ))}
            <SelectItem value="__custom__">Freie Eingabe unten …</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <p className="text-xs text-muted">
          Noch keine Live-Modelle geladen. «Modelle laden» oder «Verbindung testen» — dann erscheinen die Namen aus{' '}
          <code className="rounded bg-card-hover px-1 text-[11px]">GET /v1/models</code>.
        </p>
      )}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-mono text-sm"
        spellCheck={false}
      />
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}
